import api from './api';

/**
 * NotificationService
 * Handles user notifications for the frontend
 */
class NotificationService {
  /**
   * Get notifications for current user
   * @param {Object} params - Query parameters (isRead, limit, skip)
   * @returns {Promise} - API response with notifications
   */
  async getNotifications (params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/notifications${queryParams ? `?${queryParams}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Get unread notification count
   * @returns {Promise<number>} - Unread count
   */
  async getUnreadCount () {
    try {
      const response = await api.get('/notifications/unread-count');
      // api service returns the full response: { success: true, data: { count: 3 } }
      return response.data?.count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }

  /**
   * Mark a notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise} - API response
   */
  async markAsRead (notificationId) {
    try {
      const response = await api.patch(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   * @returns {Promise} - API response
   */
  async markAllAsRead () {
    try {
      const response = await api.post('/notifications/mark-all-read');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete a notification
   * @param {string} notificationId - Notification ID
   * @returns {Promise} - API response
   */
  async deleteNotification (notificationId) {
    try {
      const response = await api.delete(`/notifications/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Poll for new notifications
   * @param {Function} callback - Callback function to handle new notifications
   * @param {number} interval - Polling interval in milliseconds (default 30000 = 30 seconds)
   * @returns {number} - Interval ID for clearing
   */
  startPolling (callback, interval = 30000) {
    const pollNotifications = async () => {
      try {
        const count = await this.getUnreadCount();
        callback(count);
      } catch (error) {
        console.error('Error polling notifications:', error);
      }
    };

    // Poll immediately
    pollNotifications();

    // Set up interval
    return setInterval(pollNotifications, interval);
  }

  /**
   * Stop polling for notifications
   * @param {number} intervalId - Interval ID returned from startPolling
   */
  stopPolling (intervalId) {
    if (intervalId) {
      clearInterval(intervalId);
    }
  }
}

export default new NotificationService();
